import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../ui/Button';
import Input from '../ui/Input';
import StepIndicator from '../ui/StepIndicator';
import ConfirmModal from '../ui/ConfirmModal';
import ProductoFertilizanteForm from './ProductoFertilizanteForm';
import { normalizarDecimal } from '../../utils/normalizarDecimal';

const TOTAL_STEPS = 3;
const STEP_LABELS = ['Datos de aplicación', 'Productos / Mezcla', 'Resumen'];

const METODO_LABELS = { FERTIRRIEGO: 'Fertirriego', COBERTERA: 'Cobertera' };

const PRODUCTO_VACIO = { nombrecomercial: '', metodo: '', dosis_ha: '' };

function hoyISO() {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function buildInitialState() {
  return {
    fechaaplicacion: hoyISO(),
    horainicio: '',
    horafin: '',
    huerto: '',
    cuartel: '',
    superficieHa: '',
    productos: [{ ...PRODUCTO_VACIO }],
  };
}

// Formulario de Nueva aplicación de fertilizantes: 3 pasos (Datos de
// aplicación, Productos/Mezcla, Resumen), mismo UX que AplicacionForm
// (StepIndicator + wizard) pero sin paso de "Ubicación" separado — huerto y
// cuartel viven junto al resto de datos de la aplicación, como pide la
// especificación funcional. Nombre comercial es texto libre: este módulo no
// depende de ningún catálogo.
//
// Productor: mismo criterio que AplicacionForm — con 2+ productores
// asignados, selector obligatorio y visible al inicio de "Datos de
// aplicación"; con exactamente 1, NO se muestra nada (ni selector ni
// texto informativo) — la pantalla se ve idéntica a como se veía antes,
// el único idproductor se usa y envía internamente sin que el usuario lo
// note. Este formulario solo se usa en modo creación (no existe edición
// de fertilizantes), así que
// siempre se envía idproductor.
//
// HUERTO sigue siendo texto libre, sin catálogo.
function FertilizanteForm({
  productorNombre,
  productores = [],
  productoresStatus = 'ready',
  onSubmit,
  cancelTo,
}) {
  const navigate = useNavigate();
  const requiereSelectorProductor = productores.length > 1;

  const [initial] = useState(() => buildInitialState());
  const [step, setStep] = useState(1);
  const [stepError, setStepError] = useState('');

  const [idproductor, setIdproductor] = useState(() => (productores.length === 1 ? productores[0].id : ''));

  // productores llega de forma ASÍNCRONA desde la página: en el primer
  // render normalmente todavía está vacío, así que el useState de arriba
  // no alcanza a autoasignar nada. Este efecto reacciona cuando la lista
  // termina de cargar y trae exactamente 1 productor.
  useEffect(() => {
    if (productores.length === 1) {
      setIdproductor(productores[0].id);
    }
  }, [productores]);

  const [fechaaplicacion, setFechaaplicacion] = useState(initial.fechaaplicacion);
  const [horainicio, setHorainicio] = useState(initial.horainicio);
  const [horafin, setHorafin] = useState(initial.horafin);
  const [huerto, setHuerto] = useState(initial.huerto);
  const [cuartel, setCuartel] = useState(initial.cuartel);
  const [superficieHa, setSuperficieHa] = useState(initial.superficieHa);
  const [productos, setProductos] = useState(initial.productos);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showSalirConfirm, setShowSalirConfirm] = useState(false);

  const nombreProductorActual = requiereSelectorProductor
    ? productores.find((p) => String(p.id) === String(idproductor))?.nombre || ''
    : productorNombre;

  const currentSnapshot = () =>
    JSON.stringify({ idproductor, fechaaplicacion, horainicio, horafin, huerto, cuartel, superficieHa, productos });
  const initialSnapshot = JSON.stringify({ idproductor: requiereSelectorProductor ? '' : idproductor, ...initial });
  const hasChanges = () => currentSnapshot() !== initialSnapshot;

  const validateStep = (currentStep) => {
    if (currentStep === 1) {
      // Mientras el catálogo de productores todavía está cargando, no se
      // valida nada relacionado a productor: ni el selector (0 aparente)
      // ni el error de "sin productores" deben bloquear al usuario.
      if (productoresStatus === 'ready' && productores.length === 0) {
        return 'No tienes productores asignados. Contacta al administrador.';
      }
      if (requiereSelectorProductor && !idproductor) return 'Selecciona el productor.';
      if (!fechaaplicacion) return 'Ingresa la fecha de aplicación.';
      if (!horainicio) return 'Ingresa la hora de inicio.';
      if (!horafin) return 'Ingresa la hora de término.';
      if (horafin <= horainicio) return 'La hora de término debe ser posterior a la hora de inicio.';
      if (!huerto.trim()) return 'Ingresa el huerto.';
      if (!cuartel.trim()) return 'Ingresa el cuartel.';
      if (superficieHa === '' || normalizarDecimal(superficieHa) === null || normalizarDecimal(superficieHa) <= 0) {
        return 'Ingresa una superficie válida.';
      }
    }
    if (currentStep === 2) {
      if (productos.length === 0) return 'Agrega al menos un producto.';
      if (productos.some((p) => !p.nombrecomercial.trim())) return 'Ingresa el nombre comercial.';
      if (productos.some((p) => !p.metodo)) return 'Selecciona el método.';
      if (productos.some((p) => !p.dosis_ha.trim())) return 'Ingresa la dosis por hectárea.';
    }
    return '';
  };

  const handleContinuar = () => {
    const message = validateStep(step);
    if (message) {
      setStepError(message);
      return;
    }
    setStepError('');
    setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  };

  const handleAtras = () => {
    setStepError('');
    setStep((s) => Math.max(s - 1, 1));
  };

  const handleSalir = () => {
    if (hasChanges()) {
      setShowSalirConfirm(true);
    } else {
      navigate(cancelTo);
    }
  };

  const handleAgregarProducto = () => {
    setProductos((prev) => [...prev, { ...PRODUCTO_VACIO }]);
  };

  const handleCambiarProducto = (index, producto) => {
    setProductos((prev) => prev.map((p, i) => (i === index ? producto : p)));
  };

  const handleEliminarProducto = (index) => {
    setProductos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleGuardar = async () => {
    setSaving(true);
    setSaveError('');

    // Defensa: no depender solo del estado `idproductor` para el caso de
    // 1 productor — si React todavía no alcanzó a correr el efecto que lo
    // autoasigna, se resuelve aquí mismo a partir de `productores` para
    // no enviar idproductor=null al backend.
    const idProductorEfectivo = productores.length === 1 ? productores[0].id : idproductor;

    const result = await onSubmit({
      idproductor: idProductorEfectivo ? Number(idProductorEfectivo) : null,
      fechaaplicacion,
      horainicio,
      horafin,
      huerto: huerto.trim(),
      cuartel: cuartel.trim(),
      superficie_ha: normalizarDecimal(superficieHa),
      productos: productos.map((p) => ({
        nombrecomercial: p.nombrecomercial.trim(),
        metodo: p.metodo,
        dosis_ha: p.dosis_ha.trim(),
      })),
    });

    setSaving(false);

    if (!result.ok) {
      setSaveError(result.message);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <StepIndicator step={step} totalSteps={TOTAL_STEPS} label={STEP_LABELS[step - 1]} />

      {step === 1 && (
        <div className="flex flex-col gap-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6">
          <h2 className="text-lg font-semibold text-gray-900">Datos de aplicación</h2>

          {/* Con 1 solo productor asignado esta pantalla no debe mostrar
              NADA de productor: ni selector ni texto informativo — debe
              verse idéntica a como se veía antes. El productor se resuelve
              y envía internamente sin que el usuario lo note. */}
          {requiereSelectorProductor && (
            <div className="w-full">
              <label htmlFor="idproductor" className="mb-1 block text-sm font-medium text-gray-700">
                Productor *
              </label>
              <select
                id="idproductor"
                value={idproductor}
                onChange={(event) => setIdproductor(event.target.value)}
                className="min-h-[48px] w-full rounded-xl border border-gray-300 px-4 text-base text-gray-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-200"
              >
                <option value="">Seleccionar productor</option>
                {productores.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Input
              id="fechaaplicacion"
              label="Fecha *"
              type="date"
              value={fechaaplicacion}
              onChange={(event) => setFechaaplicacion(event.target.value)}
            />
            <Input
              id="horainicio"
              label="Hora inicio *"
              type="time"
              value={horainicio}
              onChange={(event) => setHorainicio(event.target.value)}
            />
            <Input
              id="horafin"
              label="Hora fin *"
              type="time"
              value={horafin}
              onChange={(event) => setHorafin(event.target.value)}
            />
            <Input
              id="huerto"
              label="Huerto *"
              value={huerto}
              onChange={(event) => setHuerto(event.target.value)}
              placeholder="Ej: Santa María"
            />
            <Input
              id="cuartel"
              label="Cuartel *"
              value={cuartel}
              onChange={(event) => setCuartel(event.target.value)}
              placeholder="Ej: C-04"
            />
            <Input
              id="superficie_ha"
              label="Superficie (ha) *"
              type="text"
              inputMode="decimal"
              value={superficieHa}
              onChange={(event) => setSuperficieHa(event.target.value)}
              placeholder="Ej: 5,5"
            />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="flex flex-col gap-4">
          <h2 className="text-lg font-semibold text-gray-900">Productos / Mezcla</h2>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {productos.map((producto, index) => (
              <ProductoFertilizanteForm
                key={index}
                index={index}
                producto={producto}
                onChange={(nuevo) => handleCambiarProducto(index, nuevo)}
                onRemove={() => handleEliminarProducto(index)}
                canRemove={productos.length > 1}
              />
            ))}
          </div>

          <Button variant="secondary" className="sm:w-auto sm:self-start sm:px-6" onClick={handleAgregarProducto}>
            + Agregar producto
          </Button>
        </div>
      )}

      {step === 3 && (
        <div className="flex flex-col gap-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6">
          <h2 className="text-lg font-semibold text-gray-900">Aplicación de Fertilizantes</h2>
          <p className="text-sm text-gray-500">
            {fechaaplicacion} · {horainicio} – {horafin}
          </p>

          <dl className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <dt className="font-medium text-gray-500">Productor</dt>
              <dd className="mt-1 text-gray-900">{nombreProductorActual}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Huerto</dt>
              <dd className="mt-1 text-gray-900">{huerto}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Cuartel</dt>
              <dd className="mt-1 text-gray-900">{cuartel}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Superficie</dt>
              <dd className="mt-1 text-gray-900">{superficieHa} ha</dd>
            </div>
          </dl>

          <div>
            <p className="text-sm font-medium text-gray-500">Mezcla</p>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {productos.map((producto, index) => (
                <div key={index} className="rounded-lg bg-gray-50 px-3 py-2">
                  <p className="text-sm font-medium text-gray-900">{producto.nombrecomercial}</p>
                  <p className="text-xs text-gray-600">
                    {METODO_LABELS[producto.metodo] || producto.metodo} · {producto.dosis_ha} / ha
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {stepError && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{stepError}</p>}
      {saveError && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{saveError}</p>}

      <div className="grid grid-cols-2 gap-3 sm:max-w-md">
        {step === 1 ? (
          <Button variant="secondary" onClick={handleSalir} disabled={saving}>
            Cancelar
          </Button>
        ) : (
          <Button variant="secondary" onClick={handleAtras} disabled={saving}>
            Atrás
          </Button>
        )}

        {step < TOTAL_STEPS ? (
          <Button onClick={handleContinuar}>Continuar</Button>
        ) : (
          <Button onClick={handleGuardar} disabled={saving}>
            {saving ? 'Guardando...' : 'Confirmar aplicación'}
          </Button>
        )}
      </div>

      <ConfirmModal
        open={showSalirConfirm}
        title="¿Salir sin guardar?"
        confirmLabel="Salir"
        cancelLabel="Seguir editando"
        onConfirm={() => navigate(cancelTo)}
        onCancel={() => setShowSalirConfirm(false)}
      >
        <p>Vas a perder los cambios ingresados en esta aplicación.</p>
      </ConfirmModal>
    </div>
  );
}

export default FertilizanteForm;
